import { useState, useEffect } from 'react';
import {
  Alert, Button, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExperimentOutlined } from '@ant-design/icons';
import httpClient from '@api/httpClient';
import { useSetEventHandlers } from '@hooks/useMetadata';
import type { FormEventHandlerDto, MetaDocumentTypeDto, MetaAttributeDto, RunFormEventResponse } from '@/types/metadata';

const { Text, Paragraph } = Typography;

const EVENT_OPTIONS = [
  { value: 'OnChange', label: 'OnChange (зміна поля шапки)' },
  { value: 'OnRowChange', label: 'OnRowChange (зміна комірки таблиці)' },
  { value: 'BeforeWrite', label: 'BeforeWrite (перед збереженням)' },
  { value: 'OnOpen', label: 'OnOpen (відкриття форми)' },
];

interface Props {
  acqType: MetaDocumentTypeDto;
}

/**
 * CRUD UI для FormEventHandler-ів (Phase 5 / Phase 6 step 1).
 * Зберігає весь масив через PUT /api/metadata/document-types/{id}/event-handlers.
 * Test-кнопка викликає POST /api/dynamic-documents/preview-event з sample header.
 */
export default function EventHandlersEditor({ acqType }: Props) {
  const [acqHandlers, setAcqHandlers] = useState<FormEventHandlerDto[]>(acqType.eventHandlers ?? []);
  const [acqEditOpen, setAcqEditOpen] = useState(false);
  const [acqEditIdx, setAcqEditIdx] = useState<number | null>(null);
  const [acqDraft, setAcqDraft] = useState<FormEventHandlerDto>({
    event: 'OnChange', attributeCode: '', expression: '', description: '',
  });
  const acqSave = useSetEventHandlers();

  useEffect(() => {
    setAcqHandlers(acqType.eventHandlers ?? []);
  }, [acqType.id, acqType.eventHandlers]);

  const acqAttrOptions = acqType.attributes
    .filter((a) => a.section === 'header')
    .map((a) => ({ value: a.code, label: `${a.name} (${a.code})` }));

  // Опції "section.fieldCode" для OnRowChange — лише атрибути табличних частин (section ≠ 'header')
  const acqRowAttrOptions = acqType.attributes
    .filter((a) => a.section && a.section !== 'header')
    .map((a) => ({ value: `${a.section}.${a.code}`, label: `${a.section} → ${a.name} (${a.code})` }));

  const acqOpenAdd = () => {
    setAcqEditIdx(null);
    setAcqDraft({ event: 'OnChange', attributeCode: '', expression: '', description: '' });
    setAcqEditOpen(true);
  };

  const acqOpenEdit = (acqIdx: number) => {
    setAcqEditIdx(acqIdx);
    setAcqDraft({ ...acqHandlers[acqIdx] });
    setAcqEditOpen(true);
  };

  const acqSaveDraft = () => {
    if (!acqDraft.expression?.trim()) { message.error('Вираз обов\'язковий'); return; }
    if (acqDraft.event === 'OnChange' && !acqDraft.attributeCode) { message.error('Для OnChange — оберіть поле'); return; }
    if (acqDraft.event === 'OnRowChange' && !acqDraft.attributeCode) { message.error('Для OnRowChange — оберіть section.field'); return; }
    const next = [...acqHandlers];
    if (acqEditIdx === null) next.push(acqDraft);
    else next[acqEditIdx] = acqDraft;
    setAcqHandlers(next);
    setAcqEditOpen(false);
  };

  const acqDelete = (acqIdx: number) => {
    setAcqHandlers(acqHandlers.filter((_, i) => i !== acqIdx));
  };

  const acqSaveAll = () => {
    acqSave.mutate({ acqTypeId: acqType.id, acqHandlers, acqTypeCode: acqType.code });
  };

  const acqColumns = [
    {
      title: 'Подія', dataIndex: 'event', key: 'event', width: 180,
      render: (v: string) => <Tag color={v === 'BeforeWrite' ? 'orange' : v === 'OnOpen' ? 'green' : 'blue'}>{v}</Tag>,
    },
    {
      title: 'Поле', dataIndex: 'attributeCode', key: 'attributeCode', width: 200,
      render: (v: string | null) => v ? <Text code>{v}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Вираз', dataIndex: 'expression', key: 'expression',
      render: (v: string) => <Text code style={{ fontSize: 11 }}>{v.length > 80 ? v.slice(0, 80) + '…' : v}</Text>,
    },
    {
      title: 'Опис', dataIndex: 'description', key: 'description',
      render: (v: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Дії', key: 'actions', width: 130,
      render: (_: unknown, _r: FormEventHandlerDto, idx: number) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => acqOpenEdit(idx)} />
          <Popconfirm title="Видалити handler?" onConfirm={() => acqDelete(idx)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Code Engine (Phase 5)"
        description={
          <div>
            Вирази на DynamicExpresso. Доступні змінні: <Text code>header</Text>, <Text code>attrs</Text>, <Text code>oldValue</Text>, <Text code>newValue</Text>.
            Хелпер: <Text code>set(header, "field", value)</Text> повертає оновлений header.
            Приклад OnChange.kolichestvo: <Text code>{'set(header, "summa", Convert.ToDecimal(newValue) * 100m)'}</Text>
          </div>
        }
      />

      <Space style={{ marginBottom: 12 }}>
        <Button icon={<PlusOutlined />} onClick={acqOpenAdd}>Додати handler</Button>
        <Button type="primary" loading={acqSave.isPending} onClick={acqSaveAll}>Зберегти всі</Button>
        <Text type="secondary">Усього: {acqHandlers.length}</Text>
      </Space>

      <Table
        rowKey={(_, i) => String(i)}
        size="small"
        columns={acqColumns}
        dataSource={acqHandlers}
        pagination={false}
      />

      <Modal
        open={acqEditOpen}
        title={acqEditIdx === null ? 'Новий handler' : 'Редагувати handler'}
        onCancel={() => setAcqEditOpen(false)}
        onOk={acqSaveDraft}
        okText="ОК"
        width={720}
      >
        <Form layout="vertical">
          <Form.Item label="Подія" required>
            <Select
              value={acqDraft.event}
              onChange={(v) => setAcqDraft({ ...acqDraft, event: v })}
              options={EVENT_OPTIONS}
            />
          </Form.Item>
          {acqDraft.event === 'OnChange' && (
            <Form.Item label="Поле (attributeCode)" required>
              <Select
                value={acqDraft.attributeCode ?? undefined}
                onChange={(v) => setAcqDraft({ ...acqDraft, attributeCode: v })}
                options={acqAttrOptions}
                showSearch
                placeholder="Оберіть з реквізитів шапки"
                filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          )}
          {acqDraft.event === 'OnRowChange' && (
            <Form.Item label="Комірка (section.fieldCode)" required help="Контекст: row (Dictionary), rows (List), rowIndex, oldValue, newValue, header, attrs, set(row,...)">
              <Select
                value={acqDraft.attributeCode ?? undefined}
                onChange={(v) => setAcqDraft({ ...acqDraft, attributeCode: v })}
                options={acqRowAttrOptions}
                showSearch
                placeholder="Оберіть колонку табличної частини"
                filterOption={(input, opt) => String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </Form.Item>
          )}
          <Form.Item label="Опис (необов'язково)">
            <Input
              value={acqDraft.description ?? ''}
              onChange={(e) => setAcqDraft({ ...acqDraft, description: e.target.value })}
              placeholder="Що робить цей handler"
            />
          </Form.Item>
          <Form.Item label="Вираз (DynamicExpresso)" required>
            <Input.TextArea
              value={acqDraft.expression}
              onChange={(e) => setAcqDraft({ ...acqDraft, expression: e.target.value })}
              rows={6}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
              placeholder='set(header, "summa", Convert.ToDecimal(newValue) * 100m)'
            />
          </Form.Item>
          <Paragraph type="secondary" style={{ fontSize: 11 }}>
            Приклади:<br/>
            • <Text code>{'set(header, "summa", Convert.ToDecimal(header["kolichestvo"]) * Convert.ToDecimal(header["tsena"]))'}</Text> — обчислити суму<br/>
            • <Text code>{'header["summa"] != null && Convert.ToDecimal(header["summa"]) > 0'}</Text> — BeforeWrite валідація<br/>
          </Paragraph>
        </Form>
      </Modal>

      <TestEventBlock acqType={acqType} acqHandlers={acqHandlers} />
    </div>
  );
}

/** Швидкий тест handler'а без збереження документа. */
function TestEventBlock({ acqType, acqHandlers }: { acqType: MetaDocumentTypeDto; acqHandlers: FormEventHandlerDto[] }) {
  const [acqOpen, setAcqOpen] = useState(false);
  const [acqEvent, setAcqEvent] = useState('OnChange');
  const [acqAttr, setAcqAttr] = useState<string | undefined>(undefined);
  const [acqHeader, setAcqHeader] = useState('{"kolichestvo": 5, "summa": 0}');
  const [acqOldValue, setAcqOldValue] = useState('0');
  const [acqNewValue, setAcqNewValue] = useState('5');
  const [acqResult, setAcqResult] = useState<RunFormEventResponse | null>(null);
  const [acqLoading, setAcqLoading] = useState(false);

  const acqRun = async () => {
    setAcqLoading(true);
    setAcqResult(null);
    try {
      const acqResp = await httpClient.post<RunFormEventResponse>('/dynamic-documents/preview-event', {
        docTypeCode: acqType.code,
        event: acqEvent,
        attributeCode: acqAttr,
        header: JSON.parse(acqHeader),
        oldValue: tryParse(acqOldValue),
        newValue: tryParse(acqNewValue),
      });
      setAcqResult(acqResp.data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail ?? e?.response?.data?.title ?? e.message);
    } finally {
      setAcqLoading(false);
    }
  };

  const acqAttrOptions = acqType.attributes
    .filter((a: MetaAttributeDto) => a.section === 'header')
    .map((a: MetaAttributeDto) => ({ value: a.code, label: a.code }));

  return (
    <div style={{ marginTop: 16 }}>
      <Button icon={<ExperimentOutlined />} onClick={() => setAcqOpen(true)} disabled={acqHandlers.length === 0}>
        Тестувати handler
      </Button>
      <Modal
        open={acqOpen}
        title="Тест event-handler'а"
        onCancel={() => setAcqOpen(false)}
        footer={null}
        width={720}
      >
        <Form layout="vertical" size="small">
          <Form.Item label="Подія">
            <Select value={acqEvent} onChange={setAcqEvent} options={EVENT_OPTIONS} />
          </Form.Item>
          {acqEvent === 'OnChange' && (
            <Form.Item label="Поле">
              <Select value={acqAttr} onChange={setAcqAttr} options={acqAttrOptions} showSearch />
            </Form.Item>
          )}
          <Form.Item label="Header (JSON)">
            <Input.TextArea
              value={acqHeader} onChange={(e) => setAcqHeader(e.target.value)}
              rows={3} style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
          </Form.Item>
          {acqEvent === 'OnChange' && (
            <>
              <Form.Item label="oldValue">
                <Input value={acqOldValue} onChange={(e) => setAcqOldValue(e.target.value)} />
              </Form.Item>
              <Form.Item label="newValue">
                <Input value={acqNewValue} onChange={(e) => setAcqNewValue(e.target.value)} />
              </Form.Item>
            </>
          )}
          <Button type="primary" onClick={acqRun} loading={acqLoading}>Запустити</Button>
          {acqResult && (
            <Alert
              type={acqResult.ok ? 'success' : 'error'}
              style={{ marginTop: 12 }}
              message={acqResult.ok ? `OK · виконано ${acqResult.handlersExecuted}` : `Помилка: ${acqResult.errorMessage}`}
              description={
                <pre style={{ fontSize: 11, margin: 0 }}>{JSON.stringify(acqResult.header, null, 2)}</pre>
              }
            />
          )}
        </Form>
      </Modal>
    </div>
  );
}

function tryParse(s: string): unknown {
  if (s === '' || s === 'null') return null;
  if (s === 'true') return true;
  if (s === 'false') return false;
  const n = Number(s);
  if (!isNaN(n) && s.trim() !== '') return n;
  try { return JSON.parse(s); } catch { return s; }
}
